/**
 * Entitlement grant rules — pure, dependency-free.
 *
 * Lives apart from useSubscription.ts on purpose: that module pulls in the
 * RevenueCat Capacitor plugin, which cannot load outside a native runtime, so
 * anything importing it is untestable from a plain script. These are the two
 * decisions worth covering with a test, so they stay free of that.
 *
 * TWO GRANT SHAPES SHARE users/{uid}.tier
 *
 *   tier 'elite', no eliteGrantExpiresAt   → founding comp, NEVER expires
 *   tier 'elite', eliteGrantExpiresAt set  → time-limited trial grant
 *
 * Absence of an expiry meaning "forever" is what leaves every existing founding
 * coach untouched, and it matches isFoundingCoach() in functions/src/index.ts.
 * Both fields are server-only (firestore.rules entitlementFields), so a client
 * cannot grant itself either shape.
 */

/**
 * Normalise a grant expiry to epoch ms. `null` means no expiry — never lapses.
 *
 * Fails OPEN: an unparseable value reads as no expiry rather than as already
 * expired. Wrongly revoking a founding or paying coach's access is a far worse
 * failure than briefly over-granting, and since nothing writes this field yet a
 * malformed value would be our own bug rather than real state.
 */
export function parseGrantExpiry(raw: any): number | null {
  if (raw === undefined || raw === null || raw === '') return null;
  if (typeof raw?.toMillis === 'function') {          // Firestore Timestamp
    try {
      const ms = raw.toMillis();
      return typeof ms === 'number' && Number.isFinite(ms) ? ms : null;
    } catch {
      return null;
    }
  }
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  if (typeof raw === 'string') {
    const t = Date.parse(raw);
    return Number.isNaN(t) ? null : t;
  }
  return null;
}

/** Does users/{uid} currently carry an active Elite grant? */
export function isGrantActive(data: any, now: number = Date.now()): boolean {
  if (!data || data.tier !== 'elite') return false;
  const expiresAt = parseGrantExpiry(data.eliteGrantExpiresAt);
  return expiresAt === null || expiresAt > now;
}

/**
 * "Mar 1, 2027" for a Season Pass end date, or null when there is nothing to show.
 *
 * null is the important case: a founding comp has no expiry and must NEVER be
 * shown an end date. Telling a comped coach their access ends is the fastest way
 * to lose one.
 */
export function formatGrantExpiry(expiresAt: number | null): string | null {
  if (expiresAt === null || !Number.isFinite(expiresAt)) return null;
  try {
    return new Date(expiresAt).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return null;
  }
}
